import _ from "lodash";
/** external */
import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { IconButton, Button, EmptyState } from "@toddle-edu/ds-web";
import {
  ChevronLeftOutlined,
  AddToLibraryOutlined,
} from "@toddle-edu/ds-icons";
import {
  NoFilterResultIllustration,
  NoSearchResultsIllustration,
} from "@toddle-edu/ds-theme";
import { connect } from "react-redux";
import update from "immutability-helper";
/** internal */
import { compose, graphql } from "lib/apolloClient";
import UIModal from "UIComponents/UIModal";
import { ToddleIcon } from "SvgComponents";
import { useI18n } from "Hooks";
import { prepareLoadMoreFeed } from "Utils";
import {
  getSchoolQuestionLibraryFeedQueryVariables,
  getToddleQuestionLibraryFeedQueryVariables,
  convertNumberToAlphabet,
  getOrganizationResources,
  getSectionTitle,
} from "Worksheet/modules/WorksheetUtils";
import {
  isWorksheetMutationsV2Enabled,
  isWorksheetQueriesV2Enabled,
  getIsToddleLibraryWorksheetQueriesV2Enabled,
} from "Worksheet/modules/WorksheetFeatureFlags";
import {
  getQuestionTopicsEnhancer,
  getQuestionPaperTypesEnhancer,
  getToddleQuestionLibraryFeedV2Enhancer,
} from "Worksheet/modules/WorksheetEnhancers";
import {
  getOrganizationGradesQuery,
  getOrganizationSubjectsQuery,
  getSchoolQuestionLibraryFeedQuery,
  getSchoolQuestionLibraryFeedQueryV2,
  getToddleQuestionLibraryFeedQuery,
  getSyllabusContentTopicsQuery,
} from "Worksheet/modules/WorksheetQueries";
import {
  getOrganizationGradesFromCache,
  getOrganizationSubjectsFromCache,
  getSchoolQuestionLibraryFeedFromCache,
  getToddleQuestionLibraryFeedFromCache,
  getSyllabusContentTopicsFromCache,
} from "Worksheet/modules/WorksheetGraphqlHelpers";
import {
  resetQuestionLibraryState,
  importQuestionFromLibrary,
  updateQuestionLibraryFilters,
  updateSelectedLibraryQuestions,
  createSection,
  createParentResource,
  createQuestionV2,
  importQuestionFromLibraryV2,
} from "Worksheet/modules/WorksheetModules";
import { getOrganizationStaffQuery } from "modules/CommonQuery";
import { getOrganizationStaffFromCache } from "modules/CommonGraphqlHelpers";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import {
  getFormattedStaffName,
  getDefaultOrderBy,
} from "Utils/GetFormattedStaffName";
import {
  QUESTION_TOPIC,
  TOPIC_DEPTH,
  SUB_TOPIC_DEPTH,
  QUESTION_PAPER,
} from "QuestionBank/modules/Constants";
import {
  getSelectedCurriculumId,
  getCurrentCurriculumProgramType,
} from "modules/CommonSelectors";
import { getGlobalGradeUid } from "LearningCourses/modules/Utils";
import {
  TODDLE_LIBRARY,
  SCHOOL_LIBRARY,
  QUESTION_IMPORT_LIMIT,
  GRADE_11_and_12,
  ORG_RESOURCE_TYPE_WORKSHEET,
  RESOURCE_TYPE_FOLDER,
  SECTION_AND_NODE_KEY,
} from "Worksheet/modules/WorksheetConstants";
import { TODDLE_LIBRARY as ACTIVE_TAB_TODDLE_LIBRARY } from "Constants/stringConstants";
import { getLibraryGradesQuery } from "ToddleLibrary/modules/ToddleLibraryQuery";
import { getLibraryGradesFromCache } from "ToddleLibrary/modules/ToddleLibraryGraphqlHelpers";
import { getMappedSubject } from "ToddleLibrary/modules/ToddleLibraryUtils";
import { MYP_GRADE_LABELS } from "ToddleLibrary/modules/Constants";
/** relative */
import classes from "./QuestionLibraryModal.scss";
import QuestionLibraryFilters from "./QuestionLibraryFilters";
import QuestionLibraryFeed from "./QuestionLibraryFeed";
import QuestionSidebarPreview from "../QuestionSidebarPreview";
import QuestionLibrarySelectedFeed from "./QuestionLibrarySelectedFeed";
import AddRandomQuestions from "./AddRandomQuestions";

const QuestionLibraryModal = props => {
  const {
    toggleQuestionLibraryModal,
    libraryData,
    totalCount,
    selectedQuestions,
    isFetchingMoreData,
    loadMoreFeed,
    isData,
    isLoading,
    hasNextPage,
    networkStatus,
    gradeOptions,
    subjectOptions,
    staffOptions,
    topics,
    isSubQuestion,
    resetQuestionLibraryState,
    updateQuestionLibraryFilters,
    defaultGrades,
    defaultSubjects,
    importQuestionFromLibrary,
    parentResourceId, // last section's resource id
    putAfterItemId,
    searchText,
    updateSelectedLibraryQuestions,
    libraryType,
    subTopics,
    parentEntity,
    createSection,
    createParentResource,
    paperTypes,
    isAddingQuestionsInBetween,
    questionResourceId,
    sectionResourceId, // current section's resouce id for adding questions in between
    parentItemId, // last section's item id
    sectionItemId, // current section's item id for adding questions in between
    questionId,
    createQuestionV2,
    importQuestionFromLibraryV2,
    isQueriesV2Enabled,
  } = props;

  const styles = {
    emptySubtitleStyle: { lineHeight: "2.4rem" },
  };
  const { t } = useI18n(["worksheet"]);

  const isMutationsV2Enabled = isWorksheetMutationsV2Enabled();
  // stores the org resource id of the question to be previewed
  const [previewResourceId, setPreviewResourceId] = useState(null);

  // stores the id of the question to be previewed
  const [previewId, setPreviewId] = useState(null);

  const [
    isSelectedQuestionsModalOpen,
    setIsSelectedQuestionsModalOpen,
  ] = useState(false);
  const [shouldApplyDefaultFilters, setShouldApplyDefaultFilters] = useState(
    true
  );

  useEffect(() => {
    return () => {
      resetQuestionLibraryState();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (shouldApplyDefaultFilters) {
      switch (libraryType) {
        case TODDLE_LIBRARY: {
          setShouldApplyDefaultFilters(false);
          break;
        }
        case SCHOOL_LIBRARY: {
          updateQuestionLibraryFilters([
            {
              key: "selectedGrades",
              value: _.map(defaultGrades, "id"),
            },
            {
              key: "selectedSubjects",
              value: _.map(defaultSubjects, "id"),
            },
          ]);
          setShouldApplyDefaultFilters(false);
          break;
        }

        default:
          break;
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryType, isData, shouldApplyDefaultFilters]);
  //WORKSHEET_RESTRUCTURING:Remove isQueriesV2Enabled once support is added in internal tool and toddle library
  const previewQuestionId = isQueriesV2Enabled ? previewId : previewResourceId;

  const handleSelectedQuestionModal = () => {
    setIsSelectedQuestionsModalOpen(prev => !prev);
  };

  const addQuestionToSelection = useCallback(
    ({ libraryCardDetails, isSelected }) => {
      if (isSelected) {
        const finalDetails = _.filter(
          selectedQuestions,
          detail => detail?.id !== libraryCardDetails?.id
        );

        updateSelectedLibraryQuestions(finalDetails);
      } else {
        const finalDetails = [...selectedQuestions, libraryCardDetails];
        updateSelectedLibraryQuestions(finalDetails);
      }
    },
    [selectedQuestions]
  );

  const onBackClick = () => {
    toggleQuestionLibraryModal();
  };

  const onAddButtonClick = async () => {
    const selectedIds = _.map(selectedQuestions, "questionId");

    const parentId = _.get(parentEntity, "id");
    const parentType = _.get(parentEntity, "type");

    let sectionId = isMutationsV2Enabled ? parentItemId : parentResourceId;

    /*
     * For worksheetCreationRevamp, we are not creating initial section, so creating initial section for add questions from library
     */
    if (_.isNil(sectionId)) {
      const sectionKey = convertNumberToAlphabet(0);

      if (isMutationsV2Enabled) {
        // Create parent resource if needed (first section)
        const parentItemId = await createQuestionV2({
          parentId,
          parentType,
          questionTypeId: SECTION_AND_NODE_KEY,
          isQueriesV2Enabled,
        });
        // Create the section if parent exists
        if (!_.isEmpty(parentItemId)) {
          sectionId = await createQuestionV2({
            parentId: parentItemId,
            parentType: "QUESTION",
            questionTypeId: SECTION_AND_NODE_KEY,
            putAfterQuestionId: putAfterItemId,
            fields: getSectionTitle(sectionKey),
          });
        }
      } else {
        // Create parent resource if needed (first section)
        const parentResourceId = await createParentResource({
          parentId,
          parentType,
          organizationResources: [
            {
              type: ORG_RESOURCE_TYPE_WORKSHEET,
              resourceType: RESOURCE_TYPE_FOLDER,
            },
          ],
        });
        // Create the section if parent exists
        if (!_.isEmpty(parentResourceId)) {
          sectionId = await createSection({
            parentId,
            parentType,
            parentResourceId: parentResourceId,
            organizationResources: getOrganizationResources(sectionKey),
            putAfterItemId: null,
          });
        }
      }
    }

    if (isMutationsV2Enabled) {
      await importQuestionFromLibraryV2({
        questionIds: selectedIds,
        putAfterItemId: isAddingQuestionsInBetween
          ? questionId
          : putAfterItemId,
        parentItemId: sectionItemId ?? sectionId,
      });
    } else {
      await importQuestionFromLibrary({
        questionIds: selectedIds,
        putAfterItemId: isAddingQuestionsInBetween
          ? questionResourceId
          : putAfterItemId,
        parentResourceId: sectionResourceId ?? sectionId,
      });
    }
    onBackClick();
  };

  const renderEmptyState = () => {
    let illustration;
    if (!searchText) {
      illustration = NoFilterResultIllustration;
    } else {
      illustration = NoSearchResultsIllustration;
    }
    return (
      <div className={classes.emptyContainer}>
        <div className={classes.emptyInnerContainer}>
          <EmptyState
            illustration={illustration}
            subtitle={t("common:emptyText_search2")}
            subtitleStyle={styles.emptySubtitleStyle}
          />
        </div>
      </div>
    );
  };

  // Function to set the preview question id
  //WORKSHEET_RESTRUCTURING:Remove setPreviewQuestionId once support is added in internal tool and toddle library
  const setPreviewQuestionId = id => {
    if (isQueriesV2Enabled) {
      setPreviewId(id);
    } else {
      setPreviewResourceId(id);
    }
  };

  const disableAddOptions = _.isEmpty(selectedQuestions);
  const disableSelection = _.size(selectedQuestions) >= QUESTION_IMPORT_LIMIT;

  return (
    <UIModal isOpen={true} isFullScreen={true}>
      <div className={classes.container}>
        {/* Header */}
        <div className={classes.header}>
          <div className={classes.headerLeftContainer}>
            <div className={classes.headerBackButtonContainer}>
              <IconButton
                icon={<ChevronLeftOutlined />}
                dsVersion="2.0"
                variant="neutral"
                type="plain"
                size="large"
                _legacy_variant="plain"
                _legacy_size="large"
                onClick={onBackClick}
              />
              <ToddleIcon width={40} height={40} />
            </div>
            <div className={"heading-5"}>
              {t("worksheet:add_questions_from_library")}
            </div>
          </div>
          <Button
            dsVersion="2.0"
            variant="neutral"
            type="outlined"
            size="large"
            _legacy_variant="outlined"
            _legacy_size="large"
            disabled={disableAddOptions}
            onClick={handleSelectedQuestionModal}
            icon={<AddToLibraryOutlined />}
            testId="question-library-questions-selected"
          >
            {t("common:no_question_selected", {
              count: _.size(selectedQuestions),
            })}
          </Button>
        </div>
        {/* Body */}
        <div className={classes.body}>
          {/* Feed */}
          <div className={classes.feed}>
            {/* FeedContainer */}
            <div className={classes.feedContainer}>
              {/* Filters */}
              <QuestionLibraryFilters
                totalCount={totalCount}
                gradeOptions={gradeOptions}
                subjectOptions={subjectOptions}
                staffOptions={staffOptions}
                topics={topics}
                subTopics={subTopics}
                isSubQuestion={isSubQuestion}
                setShouldApplyDefaultFilters={setShouldApplyDefaultFilters}
                setPreviewId={setPreviewId}
                setPreviewResourceId={setPreviewResourceId}
                paperTypes={paperTypes}
              />
              {/* Add Random Questions */}
              <AddRandomQuestions
                questionLimit={_.min([
                  QUESTION_IMPORT_LIMIT - _.size(selectedQuestions),
                  totalCount - _.size(selectedQuestions),
                ])}
                selectedQuestions={selectedQuestions}
                isSubQuestion={isSubQuestion}
              />
              {/* Feed Scroll Container */}
              {_.isEmpty(libraryData) &&
              !isLoading &&
              !shouldApplyDefaultFilters ? (
                renderEmptyState()
              ) : (
                <QuestionLibraryFeed
                  libraryData={libraryData}
                  previewQuestionId={previewQuestionId}
                  setPreviewQuestionId={setPreviewQuestionId}
                  isFetchingMoreData={isFetchingMoreData}
                  loadMoreFeed={loadMoreFeed}
                  networkStatus={networkStatus}
                  hasNextPage={hasNextPage}
                  isData={isData && !shouldApplyDefaultFilters}
                  isLoading={isLoading}
                  addQuestionToSelection={addQuestionToSelection}
                  libraryType={libraryType}
                  disableSelection={disableSelection}
                  isQueriesV2Enabled={isQueriesV2Enabled}
                />
              )}
            </div>
            {/* Footer */}
            <div className={classes.footer}>
              <Button
                dsVersion="2.0"
                variant="neutral"
                type="outlined"
                size="medium"
                _legacy_variant="outlined"
                _legacy_size="medium"
                onClick={onBackClick}
                testId="question-library-cancel"
              >
                {t("common:cancel")}
              </Button>
              <Button
                dsVersion="2.0"
                variant="progressive"
                type="fill"
                size="medium"
                _legacy_variant="progressive"
                _legacy_size="medium"
                onClick={onAddButtonClick}
                disabled={disableAddOptions}
                testId="question-library-add-to-worksheet"
              >
                {t("common:add_to_label", { label: "worksheet" })}
              </Button>
            </div>
          </div>
          {/* Preview */}
          <div className={classes.preview}>
            <QuestionSidebarPreview
              previewQuestionId={previewQuestionId}
              addQuestionToSelection={addQuestionToSelection}
              selectedQuestions={selectedQuestions}
              libraryType={libraryType}
              disableSelection={disableSelection}
            />
          </div>
          {isSelectedQuestionsModalOpen ? (
            <QuestionLibrarySelectedFeed
              handleSelectedQuestionModal={handleSelectedQuestionModal}
              selectedQuestions={selectedQuestions}
              addQuestionToSelection={addQuestionToSelection}
              t={t}
              onAddButtonClick={onAddButtonClick}
            />
          ) : null}
        </div>
      </div>
    </UIModal>
  );
};

const mapStateToProps = (state, ownProps) => {
  const { isSubQuestion } = ownProps;

  const libraryUserInfo = getUserInfo({ portalType: "COMMUNITY" });
  const libraryUserId = _.get(libraryUserInfo, "id", "");

  const libraryType = _.get(state, "worksheet.questionLibrary.libraryType", []);

  const isToddleLibraryPortal =
    state.login.activeTab === ACTIVE_TAB_TODDLE_LIBRARY;

  const userInfo = getUserInfo({ portalType: "PLANNER" });

  const organizationId = _.isEqual(libraryType, TODDLE_LIBRARY)
    ? _.get(libraryUserInfo, "org_id", "")
    : _.get(userInfo, "org_id", "");

  const questionLibraryFilters = _.get(
    state,
    "worksheet.questionLibrary.filters",
    {}
  );

  const curriculumProgramId = getSelectedCurriculumId(state);

  const parentEntity = _.get(state, "worksheet.parentEntity", {});

  const selectedQuestions = _.get(
    state,
    "worksheet.questionLibrary.selectedQuestions",
    []
  );

  const questionTopicVariables = {
    input: {
      types: [QUESTION_TOPIC],
    },
    portalType: "PLANNER",
  };

  const { selectedGrades: defaultGrades, selectedSubjects: defaultSubjects } =
    _.get(state, "worksheet.questionLibrary.defaultFilters", {}) || {};

  const { searchText = "", selectedSubjects = [] } =
    _.get(state, "worksheet.questionLibrary.filters", {}) || {};

  const currentCurriculumProgramType = isToddleLibraryPortal
    ? _.get(state, "classRoom.curriculumProgram.type")
    : getCurrentCurriculumProgramType(state);

  const gradeAndSubjectQueryVariables = {
    organizationId,
    filters: {
      curriculumProgramIds: [curriculumProgramId],
    },
    portalType: "PLANNER",
  };

  const questionLibraryFeedVariables = _.isEqual(libraryType, TODDLE_LIBRARY)
    ? getToddleQuestionLibraryFeedQueryVariables({
        userId: libraryUserId,
        parentEntity,
        questionLibraryFilters,
        isSubQuestion,
      })
    : getSchoolQuestionLibraryFeedQueryVariables({
        libraryType,
        organizationId,
        isSubQuestion,
        questionLibraryFilters,
        curriculumProgramId,
        parentEntity,
      });

  const questionPaperTypesVariables = {
    input: {
      types: [QUESTION_PAPER],
    },
    portalType: "COMMUNITY",
  };

  const isAddingQuestionsInBetween = _.get(
    state,
    "worksheet.addQuestionsInBetween.isAddingQuestionsInBetween",
    false
  );

  const questionResourceId = _.get(
    state,
    "worksheet.addQuestionsInBetween.questionResourceId",
    null
  );

  const sectionResourceId = _.get(
    state,
    "worksheet.addQuestionsInBetween.parentResourceId",
    null
  );

  const questionId = _.get(
    state,
    "worksheet.addQuestionsInBetween.questionId",
    null
  );

  const sectionItemId = _.get(
    state,
    "worksheet.addQuestionsInBetween.parentItemId",
    null
  );

  const isFromToddleLibrary = _.isEqual(libraryType, TODDLE_LIBRARY);
  // For Toddle Library, use the dedicated ToddleLibrary V2 flag; for School Library, use the worksheet V2 flag
  const isQueriesV2Enabled = isFromToddleLibrary
    ? getIsToddleLibraryWorksheetQueriesV2Enabled()
    : isWorksheetQueriesV2Enabled(isFromToddleLibrary);

  const globalGradeUid = getGlobalGradeUid();

  return {
    questionLibraryFeedVariables,
    selectedQuestions,
    organizationId,
    questionTopicVariables,
    defaultGrades,
    defaultSubjects,
    libraryType,
    searchText,
    selectedSubjects,
    gradeAndSubjectQueryVariables,
    currentCurriculumProgramType,
    isData: true,
    isLoading: false,
    parentEntity,
    questionPaperTypesVariables,
    isAddingQuestionsInBetween,
    questionResourceId,
    sectionResourceId,
    questionId,
    sectionItemId,
    isToddleLibraryPortal,
    isQueriesV2Enabled,
    globalGradeUid,
  };
};

const mapActionCreators = {
  resetQuestionLibraryState,
  updateQuestionLibraryFilters,
  importQuestionFromLibrary,
  updateSelectedLibraryQuestions,
  createSection,
  createParentResource,
  createQuestionV2,
  importQuestionFromLibraryV2,
};

QuestionLibraryModal.propTypes = {
  toggleQuestionLibraryModal: PropTypes.func,
  parentResourceId: PropTypes.string.isRequired,
};

QuestionLibraryModal.defaultProps = {
  toggleQuestionLibraryModal: _.noop,
  totalCount: 0,
};

export default compose(
  connect(mapStateToProps, mapActionCreators),
  graphql(getOrganizationSubjectsQuery, {
    name: "getOrganizationSubjects",
    skip: ({ libraryType = "" }) => !_.isEqual(libraryType, SCHOOL_LIBRARY),
    options: ({ gradeAndSubjectQueryVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: gradeAndSubjectQueryVariables,
    }),
    props: ({
      getOrganizationSubjects,
      ownProps: { gradeAndSubjectQueryVariables, isData, isLoading },
    }) => {
      const cacheData = getOrganizationSubjectsFromCache(
        gradeAndSubjectQueryVariables
      );

      const organizationSubjects = _.get(cacheData, "subjects", []);

      const subjectOptions = _.map(organizationSubjects, subject => {
        return {
          label: _.get(subject, "name", ""),
          key: _.get(subject, "id"),
          subjectGroup: _.get(subject, "subjectGroup"),
        };
      });

      return {
        subjectOptions,
        isData: !_.isEmpty(cacheData) && isData,
        isLoading:
          _.includes([1, 2], getOrganizationSubjects["networkStatus"]) ||
          isLoading,
      };
    },
  }),
  graphql(getOrganizationGradesQuery, {
    name: "getOrganizationGrades",
    skip: ({ libraryType = "" }) => !_.isEqual(libraryType, SCHOOL_LIBRARY),
    options: ({ gradeAndSubjectQueryVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: gradeAndSubjectQueryVariables,
    }),
    props: ({
      getOrganizationGrades,
      ownProps: { gradeAndSubjectQueryVariables, isData, isLoading },
    }) => {
      const cacheData = getOrganizationGradesFromCache(
        gradeAndSubjectQueryVariables
      );

      const organizationGrades = _.get(cacheData, "grades", []);

      const gradeOptions = _.map(organizationGrades, grade => {
        return {
          label: _.get(grade, "name", ""),
          value: _.get(grade, "id"),
        };
      });

      return {
        gradeOptions,
        isData: !_.isEmpty(cacheData) && isData,
        isLoading:
          _.includes([1, 2], getOrganizationGrades["networkStatus"]) ||
          isLoading,
      };
    },
  }),
  graphql(getLibraryGradesQuery, {
    name: "getLibraryGrades",
    skip: ({ libraryType = "" }) => !_.isEqual(libraryType, TODDLE_LIBRARY),
    options: ({ organizationId }) => ({
      variables: {
        id: organizationId,
        portalType: "PLANNER",
      },
      fetchPolicy: "cache-and-network",
    }),
    props: ({
      getLibraryGrades,
      ownProps: {
        isData,
        isLoading,
        organizationId,
        questionLibraryFeedVariables,
        selectedSubjects,
        globalGradeUid,
      },
    }) => {
      const organizationGradeDetails = getLibraryGradesFromCache({
        id: organizationId,
      });

      const dpGrade = _.find(
        _.get(organizationGradeDetails, "node.libraryGrades", []),
        ["label", GRADE_11_and_12]
      );

      const dpGradeId = _.get(dpGrade, "id", "");
      const librarySubjects = _.get(dpGrade, "mappedLibraryEntities", []);
      const isMypGrade = _.includes(MYP_GRADE_LABELS, globalGradeUid);

      const subjectOptions = _.map(librarySubjects, subject => {
        const subjectEntityList = _.get(
          subject,
          "mappedOrganizationEntities",
          []
        );

        return {
          label: _.get(subject, "label", ""),
          key: _.get(subject, "id"),
          orgSubjectId: getMappedSubject(subjectEntityList, isMypGrade),
        };
      });

      // send grade id in filter
      const updatedFeedVariables = !_.isEmpty(dpGradeId)
        ? update(questionLibraryFeedVariables, {
            filters: {
              grades: {
                $set: [dpGradeId],
              },
            },
          })
        : questionLibraryFeedVariables;

      const orgSubjectId = _.isEqual(_.size(selectedSubjects), 1)
        ? _.get(
            _.find(subjectOptions, ["key", _.first(selectedSubjects)]),
            "orgSubjectId",
            null
          )
        : null;

      return {
        subjectOptions,
        orgSubjectId,
        questionLibraryFeedVariables: updatedFeedVariables,
        isData: isData && !_.isEmpty(organizationGradeDetails),
        isLoading:
          isLoading || _.includes([1, 2], getLibraryGrades.networkStatus),
      };
    },
  }),
  graphql(getOrganizationStaffQuery, {
    name: "getOrganizationStaff",
    skip: ({ libraryType = "" }) => !_.isEqual(libraryType, SCHOOL_LIBRARY),
    options: ({ organizationId }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        organizationId,
        filters: {
          archivalState: "ACTIVE",
        },
        portalType: "PLANNER",
      },
    }),
    props({
      getOrganizationStaff,
      ownProps: { organizationId, isLoading, isData },
    }) {
      const cacheData = getOrganizationStaffFromCache({
        organizationId,
        filters: {
          archivalState: "ACTIVE",
        },
      });

      const defaultStaffOrderBy = getDefaultOrderBy();
      const staffOptions = _.map(
        _.get(cacheData, "node.staff.edges", []),
        staff => {
          const staffNode = _.get(staff, "node", {});
          const fullName = getFormattedStaffName({
            staff,
            orderBy: defaultStaffOrderBy,
          });

          return {
            label: fullName,
            value: _.get(staffNode, "id"),
          };
        }
      );

      return {
        staffOptions,
        isData: !_.isEmpty(cacheData) && isData,
        isLoading:
          _.includes([1, 2], getOrganizationStaff["networkStatus"]) ||
          isLoading,
      };
    },
  }),
  getQuestionTopicsEnhancer(),
  getQuestionPaperTypesEnhancer(),
  graphql(getSyllabusContentTopicsQuery, {
    name: "getSyllabusContentTopics",
    skip: ({ libraryType = "", orgSubjectId }) =>
      _.isEqual(libraryType, SCHOOL_LIBRARY) || _.isEmpty(orgSubjectId),
    options: ({ orgSubjectId }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: orgSubjectId,
        portalType: "COMMUNITY",
      },
    }),
    props({ getSyllabusContentTopics, ownProps: { isData, isLoading } }) {
      const { variables, networkStatus } = getSyllabusContentTopics;
      const cacheData = getSyllabusContentTopicsFromCache(variables);
      const data = _.get(cacheData, "associatedChildPlannerElementNodes", []);

      const topics = _.filter(data, { depth: TOPIC_DEPTH });
      const subTopics = _.filter(data, { depth: SUB_TOPIC_DEPTH });

      return {
        topics,
        subTopics,
        isData: !_.isEmpty(cacheData) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
      };
    },
  }),
  graphql(getSchoolQuestionLibraryFeedQuery, {
    name: "getSchoolQuestionLibraryFeed",
    skip: ({ libraryType = "", isQueriesV2Enabled }) =>
      !_.isEqual(libraryType, SCHOOL_LIBRARY) || isQueriesV2Enabled,
    options: ({ questionLibraryFeedVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: questionLibraryFeedVariables,
    }),
    props({
      getSchoolQuestionLibraryFeed,
      ownProps: { questionLibraryFeedVariables, isLoading, isData },
    }) {
      const cacheData = getSchoolQuestionLibraryFeedFromCache(
        questionLibraryFeedVariables
      );

      const questionLibraryDetails = _.get(
        cacheData,
        "organizationResourcesV2",
        []
      );

      const totalCount = _.get(
        questionLibraryDetails,
        "resourceTypeBasedCount.totalCount",
        0
      );

      const libraryData = _.get(questionLibraryDetails, "edges", []);

      const networkStatus = _.get(
        getSchoolQuestionLibraryFeed,
        "networkStatus"
      );
      const hasNextPage = _.get(
        questionLibraryDetails,
        "pageInfo.hasNextPage",
        false
      );

      const endCursor = _.get(questionLibraryDetails, "pageInfo.endCursor", "");

      return {
        libraryData,
        totalCount,
        isData: !_.isEmpty(cacheData) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
        hasNextPage,
        isFetchingMoreData: networkStatus == 3,
        networkStatus,
        loadMoreFeed: prepareLoadMoreFeed({
          variables: {
            ...questionLibraryFeedVariables,
            after: endCursor,
          },
          queryData: getSchoolQuestionLibraryFeed,
          key: "organizationResourcesV2",
        }),
      };
    },
  }),
  graphql(getToddleQuestionLibraryFeedQuery, {
    name: "getToddleQuestionLibraryFeed",
    skip: ({
      libraryType = "",
      questionLibraryFeedVariables,
      isQueriesV2Enabled,
    }) => {
      /** grade is compulsory for toddle library query */
      return (
        !_.isEqual(libraryType, TODDLE_LIBRARY) ||
        isQueriesV2Enabled ||
        _.isEmpty(_.get(questionLibraryFeedVariables, "filters.grades", []))
      );
    },
    options: ({ questionLibraryFeedVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: questionLibraryFeedVariables,
    }),
    props({
      getToddleQuestionLibraryFeed,
      ownProps: { questionLibraryFeedVariables, isLoading, isData },
    }) {
      const cacheData = getToddleQuestionLibraryFeedFromCache(
        questionLibraryFeedVariables
      );

      const libraryEntities = _.get(cacheData, "libraryEntities", []);

      const totalCount = _.get(libraryEntities, "totalCount", 0);

      const libraryData = _.get(libraryEntities, "edges", []);

      const networkStatus = _.get(
        getToddleQuestionLibraryFeed,
        "networkStatus"
      );
      const hasNextPage = _.get(libraryEntities, "pageInfo.hasNextPage", false);

      const endCursor = _.get(libraryEntities, "pageInfo.endCursor", "");

      return {
        libraryData,
        totalCount,
        isData: !_.isEmpty(cacheData) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
        hasNextPage,
        isFetchingMoreData: networkStatus == 3,
        networkStatus,
        loadMoreFeed: prepareLoadMoreFeed({
          variables: {
            ...questionLibraryFeedVariables,
            after: endCursor,
          },
          queryData: getToddleQuestionLibraryFeed,
          key: "libraryEntities",
        }),
      };
    },
  }),
  getToddleQuestionLibraryFeedV2Enhancer(),
  graphql(getSchoolQuestionLibraryFeedQueryV2, {
    name: "getSchoolQuestionLibraryFeedV2",
    skip: ({ libraryType = "", isQueriesV2Enabled }) =>
      !_.isEqual(libraryType, SCHOOL_LIBRARY) || !isQueriesV2Enabled,
    options: ({ questionLibraryFeedVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: questionLibraryFeedVariables,
    }),
    props({
      getSchoolQuestionLibraryFeedV2,
      ownProps: { questionLibraryFeedVariables, isLoading, isData },
    }) {
      const cacheData = _.get(getSchoolQuestionLibraryFeedV2, "node", {});

      const questionLibraryDetails = _.get(cacheData, "questions", []);

      const totalCount = _.get(questionLibraryDetails, "count.totalCount", 0);

      const libraryData = _.get(questionLibraryDetails, "edges", []);

      const networkStatus = _.get(
        getSchoolQuestionLibraryFeedV2,
        "networkStatus"
      );
      const hasNextPage = _.get(
        questionLibraryDetails,
        "pageInfo.hasNextPage",
        false
      );

      const endCursor = _.get(questionLibraryDetails, "pageInfo.endCursor", "");

      return {
        libraryData,
        totalCount,
        isData: !_.isEmpty(cacheData) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
        hasNextPage,
        isFetchingMoreData: networkStatus == 3,
        networkStatus,
        loadMoreFeed: prepareLoadMoreFeed({
          variables: {
            ...questionLibraryFeedVariables,
            after: endCursor,
          },
          queryData: getSchoolQuestionLibraryFeedV2,
          key: "questions",
        }),
      };
    },
  })
)(QuestionLibraryModal);
